import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getDailyBriefing } from "@/lib/api";
import type {
  BriefingPortfolio,
  BriefingScoreMover,
  BriefingStanceChange,
} from "@/lib/api/types";
import { Panel } from "@/components/ui/Panel";
import { Stat } from "@/components/ui/Stat";
import { DataTable } from "@/components/ui/Table";
import { Pill } from "@/components/ui/Pill";
import { Button } from "@/components/ui/Button";
import { EmptyState, LoadingState } from "@/components/ui/States";
import {
  fmtCompact,
  fmtDate,
  fmtDateTime,
  fmtPercent,
  fmtScore,
} from "@/lib/format";

export function BriefingPage() {
  const [withNarrative, setWithNarrative] = useState(true);
  const briefing = useQuery({
    queryKey: ["briefing", "daily", withNarrative],
    queryFn: () => getDailyBriefing({ include_narrative: withNarrative }),
    // Daily briefing — refetch on demand only; the LLM call is expensive.
    staleTime: 5 * 60_000,
    retry: false,
  });

  const data = briefing.data;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="space-y-0.5">
          <h1 className="text-xl font-semibold tracking-tight">Daily briefing</h1>
          <p className="text-2xs text-foreground-muted">
            {data
              ? `As of ${fmtDate(data.as_of)} · generated ${fmtDateTime(data.generated_at)}`
              : "Morning digest of P&L, risk triggers, composite movers, and stance flips."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-foreground-secondary">
            <input
              type="checkbox"
              checked={withNarrative}
              onChange={(e) => setWithNarrative(e.target.checked)}
              className="rounded border-border"
            />
            LLM narrative
          </label>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => briefing.refetch()}
            disabled={briefing.isFetching}
          >
            {briefing.isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </header>

      {briefing.isLoading && <LoadingState message="Building briefing…" />}
      {briefing.isError && (
        <EmptyState
          title="Briefing unavailable"
          description="No scores or portfolios were found. Run a scoring pass first."
        />
      )}

      {data && (
        <>
          {data.narrative && (
            <Panel
              title="Summary"
              description={
                data.narrative_model
                  ? `LLM: ${data.narrative_model}`
                  : "Auto-generated"
              }
            >
              <div className="whitespace-pre-line text-sm leading-6 text-foreground-secondary">
                {data.narrative}
              </div>
            </Panel>
          )}

          <PortfoliosSection portfolios={data.portfolios} />

          <div className="grid gap-4 lg:grid-cols-2">
            <MoversPanel
              title="Top gainers"
              description="Largest composite increases since the prior period."
              rows={data.top_gainers}
              tone="good"
            />
            <MoversPanel
              title="Top losers"
              description="Largest composite drops since the prior period."
              rows={data.top_losers}
              tone="bad"
            />
          </div>

          <StanceChangesPanel changes={data.stance_changes} />
        </>
      )}
    </div>
  );
}

function PortfoliosSection({
  portfolios,
}: {
  portfolios: BriefingPortfolio[];
}) {
  const totalMv = portfolios.reduce(
    (acc, p) => acc + (p.market_value ?? 0),
    0,
  );
  const totalPnl = portfolios.reduce(
    (acc, p) => acc + (p.pnl_1d_abs ?? 0),
    0,
  );
  const totalPriorMv = totalMv - totalPnl;
  const totalPnlPct = totalPriorMv > 0 ? totalPnl / totalPriorMv : null;
  const breachCount = portfolios.filter(
    (p) =>
      p.circuit_breaker_status === "SOFT_BREACH" ||
      p.circuit_breaker_status === "HARD_BREACH",
  ).length;

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <Panel>
          <Stat
            label="Total MV"
            value={`$${fmtCompact(totalMv)}`}
            hint={`${portfolios.length} portfolio${portfolios.length === 1 ? "" : "s"}`}
          />
        </Panel>
        <Panel>
          <Stat
            label="1d P&L"
            value={
              totalPnlPct !== null ? fmtPercent(totalPnlPct) : "—"
            }
            tone={totalPnl > 0 ? "good" : totalPnl < 0 ? "bad" : "default"}
            hint={
              totalPnl !== 0
                ? `${totalPnl > 0 ? "+" : ""}$${fmtCompact(totalPnl)}`
                : "Flat"
            }
          />
        </Panel>
        <Panel>
          <Stat
            label="Circuit-breaker breaches"
            value={breachCount}
            tone={breachCount > 0 ? "bad" : "default"}
            hint={
              breachCount === 0
                ? "All portfolios within thresholds"
                : "Manual review recommended"
            }
          />
        </Panel>
      </div>

      <Panel
        title="Portfolios"
        description="P&L and circuit-breaker status, latest close vs prior close."
      >
        {portfolios.length === 0 ? (
          <EmptyState
            title="No portfolios"
            description="Create a portfolio under /portfolio to see P&L here."
          />
        ) : (
          <DataTable
            dense
            rows={portfolios}
            rowKey={(p) => p.portfolio_id}
            columns={[
              {
                key: "name",
                header: "Portfolio",
                cell: (p) => (
                  <Link
                    to="/portfolio"
                    className="font-medium text-foreground-primary hover:text-accent-strong"
                  >
                    {p.name}
                  </Link>
                ),
              },
              {
                key: "mv",
                header: "MV",
                align: "right",
                cell: (p) =>
                  p.market_value !== null
                    ? `$${fmtCompact(p.market_value)}`
                    : "—",
              },
              {
                key: "pnl_pct",
                header: "1d %",
                align: "right",
                cell: (p) => (
                  <span
                    className={
                      p.pnl_1d_pct === null
                        ? ""
                        : p.pnl_1d_pct > 0
                          ? "text-success"
                          : p.pnl_1d_pct < 0
                            ? "text-danger"
                            : ""
                    }
                  >
                    {p.pnl_1d_pct !== null ? fmtPercent(p.pnl_1d_pct) : "—"}
                  </span>
                ),
              },
              {
                key: "pnl_abs",
                header: "1d $",
                align: "right",
                cell: (p) =>
                  p.pnl_1d_abs !== null
                    ? `${p.pnl_1d_abs > 0 ? "+" : ""}$${fmtCompact(p.pnl_1d_abs)}`
                    : "—",
              },
              {
                key: "n",
                header: "Holdings",
                align: "right",
                cell: (p) => p.n_holdings,
              },
              {
                key: "cb",
                header: "Circuit breaker",
                cell: (p) => <CircuitBreakerCell row={p} />,
              },
            ]}
          />
        )}
      </Panel>
    </div>
  );
}

function CircuitBreakerCell({ row }: { row: BriefingPortfolio }) {
  if (row.circuit_breaker_status === "HARD_BREACH") {
    return <Pill tone="bad">HARD · {fmtPercent(row.circuit_breaker_drawdown)}</Pill>;
  }
  if (row.circuit_breaker_status === "SOFT_BREACH") {
    return <Pill tone="warn">SOFT · {fmtPercent(row.circuit_breaker_drawdown)}</Pill>;
  }
  if (row.circuit_breaker_status === "OK") {
    return (
      <Pill tone="muted">
        OK{row.circuit_breaker_drawdown !== null ? ` · ${fmtPercent(row.circuit_breaker_drawdown)}` : ""}
      </Pill>
    );
  }
  if (row.circuit_breaker_status === "NO_CONFIG") {
    return <Pill tone="muted">No config</Pill>;
  }
  return <Pill tone="muted">No data</Pill>;
}

function MoversPanel({
  title,
  description,
  rows,
  tone,
}: {
  title: string;
  description: string;
  rows: BriefingScoreMover[];
  tone: "good" | "bad";
}) {
  return (
    <Panel title={title} description={description}>
      {rows.length === 0 ? (
        <EmptyState title="None" description="No movers since the prior period." />
      ) : (
        <DataTable
          dense
          rows={rows}
          rowKey={(m) => m.security_id}
          columns={[
            {
              key: "ticker",
              header: "Ticker",
              cell: (m) => (
                <Link
                  to={`/company/${m.security_id}`}
                  className="font-medium text-foreground-primary hover:text-accent-strong"
                >
                  {m.ticker ?? m.security_id}
                </Link>
              ),
            },
            {
              key: "name",
              header: "Name",
              cell: (m) => (
                <span className="text-foreground-secondary">
                  {m.name ?? "—"}
                </span>
              ),
            },
            {
              key: "prior",
              header: "Prior",
              align: "right",
              cell: (m) => fmtScore(m.prior_composite),
            },
            {
              key: "current",
              header: "Current",
              align: "right",
              cell: (m) => fmtScore(m.composite),
            },
            {
              key: "delta",
              header: "Δ",
              align: "right",
              cell: (m) => (
                <span
                  className={
                    tone === "good" ? "text-success" : "text-danger"
                  }
                >
                  {m.delta > 0 ? "+" : ""}
                  {m.delta.toFixed(1)}
                </span>
              ),
            },
          ]}
        />
      )}
    </Panel>
  );
}

function StanceChangesPanel({
  changes,
}: {
  changes: BriefingStanceChange[];
}) {
  return (
    <Panel
      title="Stance changes"
      description="Stance label flips between the prior period and now."
    >
      {changes.length === 0 ? (
        <EmptyState title="None" description="No stance flips since the prior period." />
      ) : (
        <DataTable
          dense
          rows={changes}
          rowKey={(c) => `${c.security_id}-${c.policy_version_id}`}
          columns={[
            {
              key: "ticker",
              header: "Ticker",
              cell: (c) => (
                <Link
                  to={`/company/${c.security_id}`}
                  className="font-medium text-foreground-primary hover:text-accent-strong"
                >
                  {c.ticker ?? c.security_id}
                </Link>
              ),
            },
            {
              key: "from",
              header: "From",
              cell: (c) => (
                <Pill tone="muted">{formatStanceLabel(c.from_label)}</Pill>
              ),
            },
            {
              key: "to",
              header: "To",
              cell: (c) => (
                <Pill tone="accent">{formatStanceLabel(c.to_label)}</Pill>
              ),
            },
            {
              key: "from_as_of",
              header: "Prior",
              align: "right",
              cell: (c) => fmtDate(c.from_as_of),
            },
            {
              key: "to_as_of",
              header: "Current",
              align: "right",
              cell: (c) => fmtDate(c.to_as_of),
            },
          ]}
        />
      )}
    </Panel>
  );
}

function formatStanceLabel(label: string): string {
  return label.replace(/_/g, " ");
}
